import { z } from 'zod';

/**
 * Brand-size buckets — a coarse proxy for "how big is this brand on Amazon",
 * derived from productCount + sampled review volume. Ordered smallest →
 * largest; `bucketsUpTo(cap)` returns the prefix that passes a cap.
 */
export const BRAND_SIZE_BUCKETS = ['micro', 'small', 'medium', 'large'] as const;
export const BrandSizeBucket = z.enum(BRAND_SIZE_BUCKETS);
export type BrandSizeBucket = z.infer<typeof BrandSizeBucket>;

/**
 * Return all bucket names from the smallest up to and including `cap`.
 * Used by the NL Search post-filter to expand a tenant's default cap into
 * the explicit allow-list the route applies. `bucketsUpTo('small')` →
 * `['micro', 'small']`.
 */
export function bucketsUpTo(cap: BrandSizeBucket): BrandSizeBucket[] {
  const idx = BRAND_SIZE_BUCKETS.indexOf(cap);
  return BRAND_SIZE_BUCKETS.slice(0, idx + 1) as unknown as BrandSizeBucket[];
}

/**
 * Aggregate brand stats produced by the Keepa adapter. `productCount` is
 * the authoritative size signal (Keepa /query totalResults). The sampled
 * review sum is a secondary signal kept for future bucket-rule refinement
 * — see keepa-brand-stats.ts for the resolution logic.
 */
export const BrandStats = z
  .object({
    domain: z.number().int().positive(),
    name: z.string().min(1),
    productCount: z.number().int().nonnegative(),
    sampledReviewSum: z.number().int().nonnegative().nullable(),
    sampleSize: z.number().int().nonnegative(),
    sizeBucket: BrandSizeBucket,
  })
  .strict();
export type BrandStats = z.infer<typeof BrandStats>;

/**
 * Response-side annotation attached to each product in the NL Search
 * result. `pending` means the brand wasn't in cache and a probe is firing
 * in the background — the client should re-poll for hydration. `null`
 * means the product has no brand at all (Keepa returned a blank).
 */
export const BrandSizeStatus = z.union([BrandSizeBucket, z.literal('pending')]);
export type BrandSizeStatus = z.infer<typeof BrandSizeStatus>;

/**
 * Operator decision states for a brand. Absent decision (the tenant has no
 * `brand_decisions` row for the brand) is "undecided" — not represented in
 * this enum.
 */
export const BRAND_DECISIONS = ['selected', 'rejected'] as const;
export const BrandDecision = z.enum(BRAND_DECISIONS);
export type BrandDecision = z.infer<typeof BrandDecision>;

/**
 * Request body for `PATCH /api/brands/:name/decision`. `decision: null`
 * clears any existing decision (deletes the row). `notes` is optional and
 * caps at 2000 chars to keep response payloads bounded.
 */
export const BrandDecisionPatchSchema = z
  .object({
    decision: z.union([BrandDecision, z.null()]),
    notes: z.string().max(2000).optional(),
  })
  .strict();
export type BrandDecisionPatch = z.infer<typeof BrandDecisionPatchSchema>;

/**
 * Input shape for `formatBrandReviewsLabel` — the four cache fields needed
 * to decide what to render in a "Total reviews" UI row.
 */
export interface BrandReviewsLabelInput {
  totalReviewCount: number | null;
  sampledReviewSum: number | null;
  sampleSize: number;
  productCount: number | null;
}

/**
 * Decide what to render in the "Total reviews" cell of the brand summary.
 *
 * The brand cache has two review signals:
 *   - `totalReviewCount`: authoritative, written by the deep-fetch sweep
 *     after enumerating every brand SKU (capped at 200 ASINs).
 *   - `sampledReviewSum`: the sum across `sampleSize` top-by-BSR ASINs that
 *     the initial probe hydrated.
 *
 * For brands with >200 SKUs the deep-fetch short-circuits with
 * `deep_fetch_skipped_reason='too_large'` and `totalReviewCount` stays null.
 * The historical UI rendered the literal string "estimated only" in that
 * case — operators saw no number at all, which felt like a missing field.
 *
 * This helper falls back to the sampled sum with explicit sample-size
 * context (`"14 (sample of 5 of 208)"`) so the operator always sees a
 * number plus an honest indication of how partial it is. No extrapolation
 * is performed: top-by-BSR SKUs have systematically more reviews than the
 * average SKU, so linear extrapolation would overstate. The raw sampled
 * sum + the ratio is the most defensible thing to show.
 *
 * Returns "—" when no useful signal exists at all.
 */
export function formatBrandReviewsLabel(input: BrandReviewsLabelInput): string {
  if (input.totalReviewCount !== null) {
    return input.totalReviewCount.toLocaleString();
  }
  if (
    input.sampledReviewSum !== null &&
    input.sampleSize > 0 &&
    input.productCount !== null &&
    input.productCount > 0
  ) {
    return `${input.sampledReviewSum.toLocaleString()} (sample of ${input.sampleSize} of ${input.productCount.toLocaleString()})`;
  }
  return '—';
}
